// owner-scope.ts (Story 19.6 Unit E) — the one place that answers "whose local rows are these?"
//
// THE PROBLEM. `DraftRecord` carried no owner field and the offline evaluation list read the local
// `drafts` table directly, so on a SHARED DEVICE (the clinic laptop passed between clinicians) the
// next clinician's offline list rendered the previous clinician's drafts. The PHI at rest is the
// clinical `notes`, the diagnosis and the AMPS scores; client names and DOB are NOT in IndexedDB
// (only `client_id`).
//
// WHAT THIS IS, PRECISELY. Cache hygiene and device-level data isolation. It is NOT an
// access-control boundary and must never be described as one: the frontend is a display layer
// (`docs/rules/rule-06`), and the real guarantee that clinician B cannot obtain clinician A's
// evaluations is Postgres RLS plus the endpoint's own authorization — both already in place and
// untouched here. A client-side filter is not authorization; it stops a shared device from SHOWING
// one clinician cached work belonging to another, and stops the drain from REPLAYING it under the
// wrong token.
//
// WHAT THIS DELIBERATELY IS NOT. Nothing is ever deleted — not on sign-out, not on expiry, not on a
// user change. Clearing the store at logout would wipe the `outbound_queue`, and a non-empty
// `outbound_queue` is the THIRD gate of gh-461's offline-restore (`offline-session.ts`), so wiping
// it disables that shipped mechanism outright; it would also force a product decision about
// un-synchronised work that nobody needs to make. Scope the rows to their owner; delete nothing.
//
// THE OWNER VALUE is `AuthUser.userId` (the JWT `sub`) — never `email`, which is mutable (Story
// 14.10 self-service change) and would orphan a device's rows the moment a clinician changed theirs.

import { OWNER_IDENTITY_KEY } from "./idb-schema-def";
import { db, getMeta, setMeta } from "./idb-schema";
import { ledgerKey } from "./sync-ledger";

// The pure half of the contract lives in `idb-schema-def` (beside the `owner` field itself) so the
// PURE consumers — `drain-core`, `sync-ledger` — can scope their reads without importing Dexie.
// Re-exported here so the Dexie-side readers have one import for identity + matching.
export { OWNER_IDENTITY_KEY, isOwnedBy, filterOwned, ownedRow } from "./idb-schema-def";
export type { OwnedRecord } from "./idb-schema-def";

/**
 * The owner of the CURRENT page load, mirrored in memory.
 *
 * The durable `meta.owner_identity` key remains the source of truth across page loads. (It is NOT an
 * attribution source for existing rows — the v3 `.upgrade()` deliberately reads nothing; see
 * `upgradeToV3`.) This mirror exists because the durable write is best-effort and
 * fire-and-forget at the auth chokepoint, and the gap matters in the LEAKY direction: if clinician
 * B signs in and a read reached IndexedDB before B's key write landed, the stale key would still
 * say A and B's list would be filtered to A's rows. Setting the mirror SYNCHRONOUSLY (first
 * statement of `persistOwnerIdentity`, before any await) closes that window — a read can never
 * observe the previous clinician as the current owner.
 */
let currentOwner: string | undefined;

/**
 * Stamp the device identity: set the in-memory owner synchronously, then persist the durable key
 * (best-effort — an IDB failure must never break the live auth flow).
 *
 * Called at EVERY point the app learns who is using the device: the `applyToken` success chokepoint
 * (login / forced-change setSession / every successful silent refresh) and the gh-461 offline
 * restore. NEVER unwritten: `clearOfflineIdentity()` deletes `auth_session` and deliberately leaves
 * this key alone, because the rows it describes also survive.
 */
export async function persistOwnerIdentity(userId: string): Promise<void> {
  currentOwner = userId;
  try {
    await setMeta(OWNER_IDENTITY_KEY, userId);
  } catch {
    // Best-effort — the in-memory mirror still scopes this page load correctly, and the next
    // token rotation retries the write.
  }
}

/**
 * Who owns local rows right now? The in-memory mirror when this page load has seen an auth event,
 * else the durable key (a read that runs before boot's refresh settles — e.g. a drain triggered at
 * module load).
 *
 * Fails CLOSED: `undefined` when no identity can be resolved, and `undefined` matches NO row (see
 * `isOwnedBy`), so an unresolvable owner hides everything rather than showing everything.
 */
export async function readOwnerIdentity(): Promise<string | undefined> {
  if (currentOwner) return currentOwner;
  try {
    const stored = await getMeta<unknown>(OWNER_IDENTITY_KEY);
    if (typeof stored === "string" && stored.length > 0) {
      currentOwner = stored;
      return stored;
    }
  } catch {
    // An IDB read error resolves to "no owner" → every read hides every row. Fail closed.
  }
  return undefined;
}

/**
 * Story 19.6 Unit E (19.6 review) — ADOPT the UNATTRIBUTED local rows of ONE evaluation for `owner`.
 *
 * The v3 upgrade deliberately attributes nothing (`upgradeToV3`), because a device-wide identity is
 * not evidence about an individual row. This is the recovery write: it stamps ownerless rows only.
 * Callers fall into two categories — do not conflate them:
 *   • Server-proven adoption — a 200 `GET /evaluations/{id}` under this clinician's token (past the
 *     endpoint's `clinician_id` predicate and clinician-only RLS, `useGetEvaluation`), or that
 *     clinician's own save into the row (`draft-producer`, which adopts inline because it is already
 *     inside its own `rw` transaction).
 *   • Explicit device claim (Franklin#861 — `claimUnattributedEvaluations`) — clinician
 *     self-attestation after the claim dialog confirms the unattributed drafts are theirs. This is
 *     not server proof; it is the intentional recovery door for never-synced pre-v3 residue.
 *
 * WHY THE QUEUE AND THE LEDGER ROW COME ALONG. Adopting the `drafts` row alone would make the
 * evaluation visible while its un-drained `outbound_queue` ops stayed unattributed — and the drain
 * skips unattributed ops, so the clinician's own clinical work would be permanently undrainable and
 * its sync state permanently invisible. That is half of the defect being fixed, so all three move
 * together, in ONE Dexie transaction: either the evaluation is attributed or it is not.
 *
 * ONLY `owner === undefined` IS EVER TOUCHED. A row owned by a DIFFERENT clinician is left exactly as
 * it is — this is adoption of the ownerless, never reassignment — and a falsy `owner` adopts nothing
 * (fail closed, as `isOwnedBy` does). `cache_metadata` is narrowed to the one genuinely
 * per-evaluation key, `ledger:<eval_id>`; the shared `cache:<resource>` rows are not per-evaluation
 * and are not adopted here.
 *
 * Nothing is deleted, and re-running it is a no-op.
 */
export async function adoptUnattributedEvaluationRows(
  evalId: string,
  owner: string | undefined,
): Promise<void> {
  if (!owner) return;
  await db.transaction("rw", db.drafts, db.outbound_queue, db.cache_metadata, async () => {
    const draft = await db.drafts.get(evalId);
    if (draft && draft.owner === undefined) await db.drafts.update(evalId, { owner });

    const ops = await db.outbound_queue.where("eval_id").equals(evalId).toArray();
    for (const op of ops) {
      if (op.owner === undefined) await db.outbound_queue.update(op.id, { owner });
    }

    const key = ledgerKey(evalId);
    const ledgerRow = await db.cache_metadata.get(key);
    if (ledgerRow && ledgerRow.owner === undefined) {
      await db.cache_metadata.update(key, { owner });
    }
  });
}

/**
 * Franklin#861 / DW-907 — the ids of local drafts the v3 upgrade left UNATTRIBUTED.
 *
 * Discovery keys off `drafts` only: that is the clinical body. Queued ops and `ledger:<id>` rows
 * for the same evaluation come along when `claimUnattributedEvaluations` adopts each id. Owned
 * rows (any clinician) are never listed — this is the ownerless residue, not a cross-owner scan.
 *
 * Full-table scan by design: `owner` is deliberately non-indexed (see `OwnedRecord`), and a
 * clinic laptop holds tens of drafts at most.
 */
export async function listUnattributedEvaluationIds(): Promise<string[]> {
  try {
    const rows = await db.drafts.toArray();
    return rows.filter((row) => row.owner === undefined).map((row) => row.id);
  } catch {
    return [];
  }
}

/**
 * Franklin#861 / DW-907 — the reachable door for never-synced unattributed drafts.
 *
 * Called only after an explicit clinician confirmation ("are these yours?"). Self-attestation,
 * not server proof — see `adoptUnattributedEvaluationRows`. Reuses that helper so draft + queue +
 * ledger move together, and so a foreign already-owned row is still never touched. Fail-closed on
 * a falsy `owner` (returns 0, writes nothing). Returns how many evaluation ids were claimed.
 *
 * Deliberately NOT automatic on first read — that is D9's rejected option and would restate the
 * cross-clinician stamp Unit E closed.
 */
export async function claimUnattributedEvaluations(
  owner: string | undefined,
): Promise<number> {
  if (!owner) return 0;
  const ids = await listUnattributedEvaluationIds();
  for (const id of ids) {
    await adoptUnattributedEvaluationRows(id, owner);
  }
  return ids.length;
}

/**
 * Drop the IN-MEMORY mirror only (the durable key is never deleted). Called at the single
 * unauthenticated chokepoint (`applyToken(null)`: sign-out, expiry, failed background refresh) so
 * "no session ⇒ no owner in memory" holds, and in tests to simulate a fresh page load.
 */
export function forgetCachedOwnerIdentity(): void {
  currentOwner = undefined;
}

